import React, { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { Tile } from "./waShared";

// ─── Customer Sentiment — live voice-of-customer dashboard ───────────
function SentimentPanel() {
  const [items, setItems] = useState([]);
  const [agg, setAgg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/whatsapp/sentiment/recent?limit=80");
      setItems(r.data?.items || []);
      setAgg(r.data?.aggregates || null);
    } catch (_e) { setItems([]); setAgg(null); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => filter === "all" ? items : items.filter((i) => i.type === filter), [items, filter]);

  if (loading) return <div className="card" style={{ padding: 20, textAlign: "center" }}>⏳ جارٍ التحميل...</div>;

  if (items.length === 0) {
    return (
      <div className="card" data-testid="wa-sentiment-empty"
        style={{ padding: 20, textAlign: "center", color: "var(--muted)", fontSize: 13, lineHeight: 1.9 }}>
        💛 لا توجد رسائل عملاء واردة بعد.
        <br />
        عند وصول أي رسالة WhatsApp من عميل، سيُحلَّلها الذكاء الاصطناعي تلقائياً (شكوى/مدح/سؤال) وتظهر هنا.
        <br />
        <span style={{ fontSize: 11 }}>تأكّد أن الـ Webhook متصل في إعدادات Meta.</span>
      </div>
    );
  }

  return (
    <div data-testid="wa-sentiment-panel">
      {/* Aggregate tiles */}
      {agg && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 8, marginBottom: 12 }}>
          <Tile label="الإجمالي" value={items.length} />
          <Tile label="شكاوى" value={agg.by_type?.complaint || 0} />
          <Tile label="إشادات" value={agg.by_type?.praise || 0} />
          <Tile label="استفسارات" value={agg.by_type?.question || 0} />
          <Tile label="عاجلة (high/critical)"
            value={(agg.by_urgency?.high || 0) + (agg.by_urgency?.critical || 0)} />
          <Tile label="متوسط المشاعر"
            value={agg.avg_sentiment != null ? agg.avg_sentiment : "—"} />
        </div>
      )}

      {/* Filter buttons */}
      <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
        {["all", "complaint", "praise", "question", "order_intent", "feedback", "other"].map((t) => (
          <button key={t} type="button"
            onClick={() => setFilter(t)}
            data-testid={`wa-sent-filter-${t}`}
            style={{
              padding: "5px 12px", borderRadius: 6, fontSize: 11.5, fontWeight: 700, cursor: "pointer",
              border: filter === t ? "1.5px solid var(--brown-deep)" : "1px solid var(--border)",
              background: filter === t ? "var(--brown-deep)" : "transparent",
              color: filter === t ? "#fff" : "var(--brown-deep)",
            }}>
            {t === "all" ? "الكل" : (typeLabel(t) + ` (${agg?.by_type?.[t] || 0})`)}
          </button>
        ))}
        <button type="button" onClick={load} className="btn btn-soft" style={{ fontSize: 11, marginInlineStart: "auto" }}>🔄 تحديث</button>
      </div>

      {/* Sentiment cards */}
      <div style={{ display: "grid", gap: 8 }}>
        {filtered.map((s) => (
          <div key={s.id} className="card" style={{
            padding: 12,
            borderLeft: `4px solid ${urgencyColor(s.urgency)}`,
            background: s.type === "complaint" ? "#fef2f2" : (s.type === "praise" ? "#f0fdf4" : "#fff"),
          }} data-testid={`wa-sent-item-${s.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
              <div>
                <span style={{ fontSize: 14, fontWeight: 800, color: "var(--brown-deep)" }}>{typeEmoji(s.type)} {typeLabel(s.type)}</span>
                <span style={{ fontSize: 11, color: urgencyColor(s.urgency), fontWeight: 700, marginInlineStart: 8 }}>
                  ⚠ {urgencyLabel(s.urgency)}
                </span>
              </div>
              <span style={{ fontSize: 11, color: "var(--muted)" }}>
                {s.from} · {(s.analyzed_at || "").slice(0, 16).replace("T", " ")}
              </span>
            </div>
            {s.topic && (
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--brown-deep)", marginBottom: 4 }}>
                📌 {s.topic}
              </div>
            )}
            <div style={{ fontSize: 12.5, color: "#475569", lineHeight: 1.7, marginBottom: 6 }}>
              <b>الرسالة:</b> {s.message_body}
            </div>
            {s.summary && (
              <div style={{ fontSize: 12, color: "#0369a1", lineHeight: 1.6, marginBottom: 6 }}>
                <b>الملخص:</b> {s.summary}
              </div>
            )}
            {s.suggested_reply && (
              <details style={{ fontSize: 12, color: "#16a34a", marginTop: 4 }}>
                <summary style={{ cursor: "pointer", fontWeight: 700 }}>💬 رد مقترح من المساعد</summary>
                <div style={{ padding: "6px 10px", background: "#f0fdf4", borderRadius: 6, marginTop: 4 }}>
                  {s.suggested_reply}
                </div>
              </details>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function typeLabel(t) {
  return { complaint: "شكوى", praise: "إشادة", question: "استفسار", order_intent: "نية طلب", feedback: "ملاحظة", other: "أخرى" }[t] || t;
}
function typeEmoji(t) {
  return { complaint: "😡", praise: "💛", question: "❓", order_intent: "🛒", feedback: "💬", other: "📝" }[t] || "📝";
}
function urgencyLabel(u) {
  return { low: "منخفضة", medium: "متوسطة", high: "عالية", critical: "حرجة" }[u] || u;
}
function urgencyColor(u) {
  return { low: "#94a3b8", medium: "#f59e0b", high: "#dc2626", critical: "#7c2d12" }[u] || "#94a3b8";
}

export default SentimentPanel;
